import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Clock,
  Calendar as CalIcon,
  Paperclip,
  CheckCircle2,
  Building2,
  Trash2,
  Loader2,
} from "lucide-react";
import { StatusBadge, PriorityBadge } from "@/components/Badges";
import { STATUS_META, fmtDate } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export function TaskCard({ task, index = 0 }) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === "admin";
  const m = STATUS_META[task.status] || STATUS_META.pendiente;

  const handleDelete = async (e) => {
    e.stopPropagation();

    if (deleting) return;

    const confirmed = window.confirm(
      `¿Eliminar la tarea "${task.title}"?\n\nEsta acción no se puede deshacer.`
    );

    if (!confirmed) return;

    setDeleting(true);

    try {
      await api.delete(`/tasks/${task.id}`);

      toast.success("Tarea eliminada correctamente");

      window.location.reload();
    } catch (error) {
      toast.error(
        apiError(error?.response?.data?.detail) ||
          "No se pudo eliminar la tarea"
      );
      setDeleting(false);
    }
  };

  return (
    <button
      type="button"
      data-testid={`task-card-${task.id}`}
      onClick={() => {
        if (!deleting) navigate(`/tareas/${task.id}`);
      }}
      style={{ animationDelay: `${Math.min(index * 40, 400)}ms` }}
      className={cn(
        "group w-full animate-fade-up rounded-xl border border-slate-200 border-l-4 bg-white p-4 text-left shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md",
        m.leftBorder
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="mb-1 flex flex-wrap items-center gap-2">
            <PriorityBadge priority={task.priority} />

            {task.requires_evidence && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400">
                <Paperclip className="h-3 w-3" />
                Evidencia
              </span>
            )}

            {task.requires_approval && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-400">
                <CheckCircle2 className="h-3 w-3" />
                Aprobación
              </span>
            )}
          </div>

          <h3 className="truncate font-heading text-base font-bold text-slate-900">
            {task.title}
          </h3>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              <CalIcon className="h-3.5 w-3.5" />
              {fmtDate(task.date)}
            </span>

            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" />
              {task.time} · vence {task.due_time}
            </span>

            {task.department && (
              <span className="inline-flex items-center gap-1">
                <Building2 className="h-3.5 w-3.5" />
                {task.department.name}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <StatusBadge status={task.status} />

          {isAdmin && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleDelete}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleDelete(e);
                }
              }}
              title="Eliminar tarea"
              aria-label={`Eliminar tarea ${task.title}`}
              data-testid={`delete-task-${task.id}`}
              className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
            </span>
          )}
        </div>
      </div>

      {task.assignee && (
        <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-2">
          <div className="flex h-6 w-6 items-center justify-center overflow-hidden rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">
            {task.assignee.avatar ? (
              <img
                src={task.assignee.avatar}
                alt=""
                className="h-full w-full object-cover"
              />
            ) : (
              task.assignee.name?.[0]
            )}
          </div>

          <span className="text-xs font-medium text-slate-600">
            {task.assignee.name}
          </span>
        </div>
      )}
    </button>
  );
}